import { useIsLoggedIn } from '@emico-hooks/login-token'
import { Picture } from '@emico-react/image'
import classNames from 'classnames'

import { usePrismicDocument } from '@emico/prismic'
import { PrismicDocumentLinkType } from '@emico/prismic/src/responseTypes'

import {
    CategoryBodyLoyaltyCardPrimary,
    LoyaltyCard,
    Usps,
} from './graphql/schema.generated'
import FadeTransition from './layout/FadeTransition'
import MemberUsps from './MemberUsps'
import styles from './PrismicCategoryLoyaltyCard.module.scss'
import breakpoints from './theme/breakpoints'
import ButtonVariant from './types/ButtonVariant'
import Heading from './typography/Heading'

export const PrismicCategoryLoyaltyCard = ({
    guestCard,
    memberCard,
}: CategoryBodyLoyaltyCardPrimary) => {
    const isLoggedIn = useIsLoggedIn()

    const { id: cardLinkId } = isLoggedIn
        ? (memberCard as PrismicDocumentLinkType)
        : (guestCard as PrismicDocumentLinkType)

    const { data } = usePrismicDocument<LoyaltyCard>({
        key: 'document.id',
        value: cardLinkId,
    })
    return (
        <FadeTransition>
            {data ? <CategoryLoyaltyCard {...data} /> : null}
        </FadeTransition>
    )
}

const CategoryLoyaltyCard = (card: LoyaltyCard) => {
    const {
        header,
        image,
        mobileImage,
        buttonLabel,
        buttonStyle,
        buttonUrl,
        usp,
        style,
    } = card

    const { id: uspLinkId } = usp as PrismicDocumentLinkType
    const { data: uspData } = usePrismicDocument<Usps>({
        key: 'document.id',
        value: uspLinkId,
    })

    const buttonVariant =
        buttonStyle?.toLowerCase() === 'primary'
            ? ButtonVariant.primary
            : ButtonVariant.secondary

    const mobileImageUrl = mobileImage?.url
    const imageUrl = image?.url

    if (!imageUrl) {
        return null
    }

    return (
        <div
            className={classNames(
                styles.card,
                style?.toLowerCase() === 'light' ? styles.light : styles.dark,
            )}
        >
            <Picture
                breakpoints={{
                    [breakpoints.xs]: {
                        url: mobileImageUrl ?? imageUrl,
                        width: 767,
                        height: undefined,
                        params: {
                            resizingType: 'fill',
                        },
                    },
                    [breakpoints.md]: {
                        url: imageUrl,
                        width: 991,
                        height: undefined,
                        params: {
                            resizingType: 'fill',
                        },
                    },
                    [breakpoints.lg]: {
                        url: imageUrl,
                        width: 2000,
                        height: undefined,
                        params: {
                            resizingType: 'fill',
                        },
                    },
                }}
                lazy
                alt={header || ''}
                className={styles.image}
            />

            <MemberUsps
                usps={uspData?.usp ?? []}
                containerClassname={styles.section}
                wrapperClassname={styles.wrapper}
                listClassname={styles.list}
                heading={
                    <Heading
                        color={
                            style?.toLowerCase() === 'light' ? 'light' : 'dark'
                        }
                        className={styles.header}
                        element="h2"
                        variant="h2"
                        fuss={['pme']}
                    >
                        {header}
                    </Heading>
                }
                uspIconClassname={styles.icon}
                buttonClassname={styles.buttons}
                uspTextColor={
                    style?.toLowerCase() === 'light' ? 'light' : 'dark'
                }
                buttonText={buttonLabel ?? ''}
                buttonStyle={buttonVariant}
                buttonUrl={buttonUrl ?? ''}
            />
        </div>
    )
}
